import {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  MutableRefObject
} from 'react'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import {
  Radio,
  Typography,
  Pagination,
  Button,
  Input,
  Space,
  Tooltip,
  Modal,
  notification
} from 'antd'
import * as Icons from '@ant-design/icons'
import querystring from 'query-string'
import styled from 'styled-components'
import _ from 'lodash'

import * as ROUTES from 'admin/routes'

import { useApi, useApiQuery } from 'admin/lib/Api'
import { SearchHighlightedText } from 'admin/lib/Icons/SearchHighlightedText'
import {
  ContentContainer,
  ContentFooter,
  ContentCentred,
  Fill
} from 'admin/lib/Layouts'

import { useGlobalShortcut } from 'admin/lib/shortcuts'
import { Matchers } from 'admin/lib/Records'
import { moduleNameFromId } from 'admin/lib/constants'

import { PDFViewer } from './PDFViewer'
import { ImageViewer } from './ImageViewer'
import { ContentBodyWithMarker } from './ContentBodyWithMarker'
import { colours } from '@qflow/theme'

type OcrData = {
  angle?: number[]
  height?: number[]
  width?: number[]
  subRegions?: any[]
}

type AttachmentViewProps = {
  record: any
  handleExtractionValueClicked: (event) => void
  currentStatus: string
  projectLocale: string
}

export function AttachmentView({
  record,
  handleExtractionValueClicked,
  currentStatus = '',
  projectLocale
}: AttachmentViewProps) {
  const [index, setIndex] = useState(1)
  const [markerEnabled, setMarkerEnabled] = useState(false)
  const [ocrBoxesEnabled, setOcrBoxesEnabled] = useState(true)
  const [reloadPage, setReload] = useState(false)
  const [regions, setRegions] = useState(Array(record.attachments.length))
  const [splitRecordModalOpen, setSplitRecordModalOpen] = useState(false)
  const [splitRecordModalStatus, setSplitRecordModalStatus] = useState({
    loading: false,
    error: false
  })
  const [ocrSearchText, setOcrSearchText] = useState('')
  const [highlightSearchEnabled, setHighlightSearchEnabled] = useState(false)
  const [currentZoomLevel, setCurrentZoomLevel] = useState(1)
  const [processingZoom, setProcessingZoom] = useState(false)
  const [rotations, setRotations] = useState({})

  const navigate = useNavigate()

  const lastFocus = useRef<HTMLInputElement>() as MutableRefObject<
    HTMLInputElement | undefined | null
  >

  const boxRef = useRef<HTMLDivElement>(null)

  const rememberFocus = useCallback(e => {
    lastFocus.current = document.activeElement as HTMLInputElement
  }, [])

  const returnFocus = useCallback(() => {
    if (lastFocus.current) {
      lastFocus.current?.focus?.()
    }
  }, [])

  const attachment = useMemo(() => {
    return record.attachments[index - 1]
  }, [index, record.attachments])
  const noAttachments = record.attachments?.length === 0
  const hasMultipleAttachments = record.attachments?.length > 1
  const attachmentType = getType(attachment?.name)

  const { accountId, projectId, recordId } = useParams<{
    accountId: string
    projectId: string
    recordId: string
  }>()

  const updateIndex = useCallback(
    (newIndex: number) => {
      setIndex(newIndex)
      sessionStorage.setItem(
        `${recordId}-current-page-index`,
        JSON.stringify(newIndex)
      )
    },
    [recordId]
  )

  useEffect(() => {
    setHighlightSearchEnabled(
      localStorage.getItem('highlightSearchEnabled') ? true : false
    )

    const storedZoomLevel = sessionStorage.getItem(
      `${recordId}-current-zoom-level`
    )
    if (storedZoomLevel) {
      setCurrentZoomLevel(JSON.parse(storedZoomLevel))
    }

    const storedRotations = sessionStorage.getItem(`${recordId}-rotations`)
    if (storedRotations) {
      setRotations(JSON.parse(storedRotations))
    }

    const storedIndex = sessionStorage.getItem(`${recordId}-current-page-index`)
    if (storedIndex) {
      setIndex(JSON.parse(storedIndex))
    }
  }, [recordId])

  const { loaded: ocrLoaded, data: ocrData } = useApiQuery(
    useCallback(
      api => {
        if (noAttachments) {
          return new Promise(() => ({ ocrLoaded: true, ocrData: {} }))
        }
        return api.getOcr(
          accountId ? accountId : '',
          projectId ? projectId : '',
          recordId ? recordId : '',
          attachment.attachmentId
        )
      },
      [accountId, attachment, noAttachments, projectId, recordId]
    )
  ) as { loaded: boolean; data: OcrData | null }

  if (ocrLoaded && regions[index - 1] !== ocrData) {
    const newRegions = [...regions]
    newRegions[index - 1] = ocrData
    setRegions(newRegions)
  }

  const handleRotationChange = useCallback(
    degrees => {
      if (noAttachments) {
        return
      }
      setRotations(rotations => {
        return {
          ...rotations,
          [attachment.attachmentId]:
            (rotations[attachment.attachmentId] || 0) + degrees
        }
      })
      sessionStorage.setItem(
        `${recordId}-rotations`,
        JSON.stringify({
          ...rotations,
          [attachment.attachmentId]:
            (rotations[attachment.attachmentId] || 0) + degrees
        })
      )
    },
    [attachment, noAttachments, recordId, rotations]
  )

  const handleOpenImageClick = useCallback(() => {
    openInPopup(attachment?.name, attachment?.readUri)
  }, [attachment?.name, attachment?.readUri])

  const api = useApi()
  const handleRecordSplitRequest = async () => {
    if (api) {
      setSplitRecordModalStatus({
        loading: true,
        error: false
      })

      // moduleNameFromId return 'delivery' or 'waste'
      const lowercaseModuleName = moduleNameFromId(
        record['moduleTagDefinitionId']
      )

      const moduleName =
        lowercaseModuleName === 'delivery' ? 'Delivery' : 'Waste'

      const [data, status, error] = await api.postSplitRecord(
        accountId ? accountId : '',
        lowercaseModuleName,
        projectId ? projectId : '',
        recordId ? recordId : ''
      )

      if (status === 200 || status === 204) {
        setSplitRecordModalStatus({
          loading: false,
          error: false
        })
        setSplitRecordModalOpen(false)

        notification.success({
          message: 'Success! 💪😎🍹',
          description: `Record voided and created ${data} new records. New Records will show up on the intray shortly. Click this notification to go back to the intray.`,
          onClick: () => {
            navigate(ROUTES.INTRAY)
          }
        })

        navigate(
          generatePath(ROUTES.RECORD.EDIT, {
            accountId: accountId,
            projectId: projectId,
            moduleName: moduleName,
            recordId: recordId
          })
        )
      }

      if (error) {
        setSplitRecordModalStatus({
          loading: false,
          error: true
        })
      }
    }
  }

  const getCircularIndex = useCallback(
    index => {
      if (index > record.attachments.length) {
        return 1
      }
      if (index < 1) {
        return record.attachments.length
      }
      return index
    },
    [record.attachments.length]
  )

  useEffect(() => {
    const checkHighlightedText = () => {
      if (highlightSearchEnabled) {
        const selection = window.getSelection()
        if (selection && selection.toString().length > 0) {
          setOcrSearchText(selection.toString().trim())
        }
      }
    }
    const checkKeyboardHighlightedText = (event: KeyboardEvent) => {
      if (highlightSearchEnabled && event.shiftKey) {
        checkHighlightedText()
      }
    }
    const checkInputFocusedHighlightedText = () => {
      if (
        highlightSearchEnabled &&
        document.activeElement instanceof HTMLInputElement
      ) {
        const inputElement = document.activeElement as HTMLInputElement
        if (inputElement.value.length > 0) {
          setOcrSearchText(inputElement.value.trim())
        }
      }
    }

    window.addEventListener('mouseup', checkHighlightedText)
    window.addEventListener('dblclick', checkHighlightedText)
    window.addEventListener('keyup', checkKeyboardHighlightedText)
    window.addEventListener('focusin', checkInputFocusedHighlightedText)
    return () => {
      window.removeEventListener('mouseup', checkHighlightedText)
      window.removeEventListener('dblclick', checkHighlightedText)
      window.removeEventListener('keyup', checkKeyboardHighlightedText)
      window.removeEventListener('focusin', checkInputFocusedHighlightedText)
    }
  }, [highlightSearchEnabled, setOcrSearchText])

  const handleSearch = value => {
    if (value && value.length > 0) {
      setOcrSearchText(value.trim())
      localStorage.setItem('ocrSearchText', value.trim())
      setHighlightSearchEnabled(true)
    } else {
      setOcrSearchText('')
      localStorage.removeItem('ocrSearchText')
      setHighlightSearchEnabled(false)
    }
  }

  const handleHighlightSearchEnabledClick = e => {
    if (!highlightSearchEnabled) {
      setHighlightSearchEnabled(true)
      localStorage.setItem('highlightSearchEnabled', 'true')
    } else {
      setHighlightSearchEnabled(false)
      localStorage.removeItem('highlightSearchEnabled')
      setOcrSearchText('')
    }

    returnFocus()
  }

  const zoomLevels = useMemo(() => [0.5, 0.75, 1, 1.5, 2, 2.5, 3], [])
  const resetZoomLevel = () => {
    setCurrentZoomLevel(1)
    sessionStorage.setItem(`${recordId}-current-zoom-level`, JSON.stringify(1))
  }
  const increaseZoomLevel = useCallback(() => {
    const debouncedZoom = _.debounce(
      () => {
        // get index of current zoom level
        const currentIndex = zoomLevels.indexOf(currentZoomLevel)
        // get next zoom level
        const nextZoomLevel =
          zoomLevels[
            currentIndex + 1 > zoomLevels.length - 1
              ? currentIndex
              : currentIndex + 1
          ]
        // set next zoom level
        setCurrentZoomLevel(nextZoomLevel)
        sessionStorage.setItem(
          `${recordId}-current-zoom-level`,
          JSON.stringify(nextZoomLevel)
        )
      },
      500,
      { leading: true, trailing: false }
    )

    debouncedZoom()
  }, [currentZoomLevel, recordId, zoomLevels])

  const decreaseZoomLevel = useCallback(() => {
    const debouncedZoom = _.debounce(
      () => {
        // get index of current zoom level
        const currentIndex = zoomLevels.indexOf(currentZoomLevel)
        // get previous zoom level
        const previousZoomLevel =
          zoomLevels[currentIndex - 1 < 0 ? 0 : currentIndex - 1]
        // set previous zoom level
        setCurrentZoomLevel(previousZoomLevel)
        sessionStorage.setItem(
          `${recordId}-current-zoom-level`,
          JSON.stringify(previousZoomLevel)
        )
      },
      500,
      { leading: true, trailing: false }
    )

    debouncedZoom()
  }, [currentZoomLevel, recordId, zoomLevels])

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey) {
        event.preventDefault()
        if (processingZoom) {
          return
        }
        setProcessingZoom(true)
        //control key is pressed
        if (event.deltaY > 0) {
          //scrolled down
          decreaseZoomLevel()
        } else {
          //scrolled up
          increaseZoomLevel()
        }
        setTimeout(() => {
          setProcessingZoom(false)
        }, 500)
      }
    }

    const element = boxRef.current
    if (element) {
      element.addEventListener('wheel', handleWheel, {
        capture: true,
        passive: false
      })
    }

    return () => {
      if (element) {
        element.removeEventListener('wheel', handleWheel)
      }
    }
  }, [decreaseZoomLevel, increaseZoomLevel, processingZoom])

  useGlobalShortcut(
    Matchers.image.previous,
    useCallback(() => {
      updateIndex(getCircularIndex(index - 1))
    }, [getCircularIndex, index, updateIndex])
  )

  useGlobalShortcut(
    Matchers.image.next,
    useCallback(() => {
      updateIndex(getCircularIndex(index + 1))
    }, [getCircularIndex, index, updateIndex])
  )

  useGlobalShortcut(
    Matchers.image.rotateRight,
    useCallback(() => {
      handleRotationChange(90)
    }, [handleRotationChange])
  )

  useGlobalShortcut(
    Matchers.image.rotateLeft,
    useCallback(() => {
      handleRotationChange(-90)
    }, [handleRotationChange])
  )

  useGlobalShortcut(
    Matchers.image.toggleMarker,
    useCallback(() => {
      setMarkerEnabled(enabled => !enabled)
    }, [])
  )

  useGlobalShortcut(
    Matchers.image.toggleOcrBoxes,
    useCallback(() => {
      setOcrBoxesEnabled(enabled => !enabled)
    }, [])
  )

  return (
    <ContentContainer className={'Layouts__ContentContainer'} ref={boxRef}>
      {noAttachments ? (
        <ContentBodyWithMarker enabled={false}>
          <HalfWidthCenteredDiv>
            <Typography.Title level={3}>
              Record has no attachments{' '}
              <span role="img" aria-label="face screaming in fear">
                😱
              </span>
            </Typography.Title>
          </HalfWidthCenteredDiv>
        </ContentBodyWithMarker>
      ) : (
        <>
          {record.attachments.length > 1 && (
            <ContentCentred style={{ margin: '1rem 0' }}>
              <Pagination
                defaultCurrent={index}
                current={index}
                onChange={page => updateIndex(page)}
                pageSize={1}
                total={record.attachments.length}
                style={{ fontWeight: 600, fontSize: '1rem' }}
              />
            </ContentCentred>
          )}

          <Controls
            resetZoomLevel={resetZoomLevel}
            increaseZoomLevel={increaseZoomLevel}
            decreaseZoomLevel={decreaseZoomLevel}
            rememberFocus={rememberFocus}
            returnFocus={returnFocus}
          />
          <ContentBodyWithMarker
            enabled={markerEnabled}
            currentZoomLevel={currentZoomLevel}
            recordId={recordId}
          >
            <ContentViewer
              attachment={attachment}
              rotation={rotations[attachment?.attachmentId] || 0}
              ocrData={ocrLoaded ? regions[index - 1] : null}
              ocrEnabled={ocrBoxesEnabled}
              handleExtractionValueClicked={handleExtractionValueClicked}
              ocrSearchText={highlightSearchEnabled ? ocrSearchText : ''}
              currentZoomLevel={currentZoomLevel}
              attachmentIndex={index}
              hasMultipleAttachments={hasMultipleAttachments}
              projectLocale={projectLocale}
            />
          </ContentBodyWithMarker>
        </>
      )}

      <ContentFooter>
        <Space>
          <Radio.Group
            value={0}
            onChange={e => {
              handleRotationChange(e.target.value)
              returnFocus()
            }}
            onMouseEnter={rememberFocus}
          >
            <Tooltip title="Rotate anticlockwise">
              <Radio.Button value={-90}>
                <Icons.RotateLeftOutlined />
              </Radio.Button>
            </Tooltip>
            <Tooltip title="Rotate clockwise">
              <Radio.Button value={90}>
                <Icons.RotateRightOutlined />
              </Radio.Button>
            </Tooltip>
          </Radio.Group>
          <Tooltip title="Reload image">
            <Radio.Button
              name="Reload"
              checked={reloadPage}
              onClick={() => {
                setReload(checked => !checked)
                returnFocus()
              }}
              onMouseEnter={rememberFocus}
            >
              <Space>
                <Icons.SyncOutlined />
              </Space>
            </Radio.Button>
          </Tooltip>
          <Radio.Button
            name="Position Marker"
            checked={markerEnabled}
            onClick={() => {
              setMarkerEnabled(checked => !checked)
              returnFocus()
            }}
            onMouseEnter={rememberFocus}
          >
            <Space>
              {markerEnabled ? (
                <Icons.EyeFilled />
              ) : (
                <Icons.EyeInvisibleFilled />
              )}
              Marker
            </Space>
          </Radio.Button>

          <Radio.Button
            name="OCR Boxes"
            checked={ocrBoxesEnabled}
            onClick={() => {
              setOcrBoxesEnabled(checked => !checked)
              returnFocus()
            }}
            onMouseEnter={rememberFocus}
          >
            <Space>
              {ocrBoxesEnabled ? (
                <Icons.EyeFilled />
              ) : (
                <Icons.EyeInvisibleFilled />
              )}
              OCR
            </Space>
          </Radio.Button>
          <Input.Search
            placeholder="Search image"
            onSearch={handleSearch}
            style={{ width: '14vw' }}
          />
          <Tooltip title="Enable/Disable highlighting of selected text">
            <Radio.Button
              name="highlight-search"
              checked={highlightSearchEnabled}
              onClick={handleHighlightSearchEnabledClick}
              onMouseEnter={rememberFocus}
            >
              <SearchHighlightedText />
            </Radio.Button>
          </Tooltip>
          <Tooltip title="Download image">
            <Button
              onClick={handleOpenImageClick}
              disabled={!attachment?.readUri}
              type="primary"
              icon={<Icons.ExportOutlined />}
            />
          </Tooltip>
        </Space>
        <Fill />
        {record.attachments.length > 1 && (
          <Space>
            <Pagination
              simple
              defaultCurrent={1}
              current={index}
              onChange={page => updateIndex(page)}
              pageSize={1}
              total={record.attachments.length}
              size="small"
              style={{ fontWeight: 600, fontSize: '1rem' }}
            />
          </Space>
        )}
        <Fill />
        <Space>
          <Radio.Button
            name="Split Record"
            disabled={currentStatus.toLowerCase() === 'void'}
            onClick={() => {
              setSplitRecordModalStatus({
                loading: false,
                error: false
              })
              setSplitRecordModalOpen(true)
            }}
            onMouseEnter={rememberFocus}
          >
            <Modal
              title="Split Record"
              open={splitRecordModalOpen}
              onOk={handleRecordSplitRequest}
              confirmLoading={splitRecordModalStatus.loading}
              onCancel={() => {
                setSplitRecordModalOpen(false)
              }}
              footer={[
                <Button
                  key="cancel"
                  onClick={() => setSplitRecordModalOpen(false)}
                >
                  Cancel
                </Button>,
                isPDF(attachment?.name) && (
                  <Button
                    key="advanced"
                    onClick={() => {
                      const moduleName = moduleNameFromId(
                        record['moduleTagDefinitionId']
                      )
                      navigate(
                        generatePath(ROUTES.PDF_SPLITTER.SPLIT_RECORD, {
                          accountId: accountId,
                          projectId: projectId,
                          moduleName: moduleName,
                          recordId: recordId,
                          attachmentIndex: String(index)
                        }),
                        {
                          state: {
                            attachmentRotations: ocrLoaded ? ocrData?.angle : []
                          }
                        }
                      )
                    }}
                  >
                    Advanced Splitting
                  </Button>
                ),
                <Button
                  key="ok"
                  type="primary"
                  loading={splitRecordModalStatus.loading}
                  onClick={handleRecordSplitRequest}
                >
                  OK
                </Button>
              ]}
            >
              Splitting will:
              <ul>
                <li>
                  This will create <b>multiple records</b>
                  {isPDF(attachment?.name) && ', one for each page in the PDF'}.
                </li>
                <li>
                  It will <b>void</b> the current Record
                </li>
              </ul>
              <Typography>Continue?</Typography>
              {splitRecordModalStatus.error && (
                <ErrorDiv>
                  There was an issue splitting this record. Please try again
                </ErrorDiv>
              )}
            </Modal>

            <Space>
              <Icons.SplitCellsOutlined />
              Split Record
            </Space>
          </Radio.Button>
        </Space>
        <Space style={{ paddingLeft: 5 }}>
          <Typography.Text type="secondary">{attachmentType}</Typography.Text>
        </Space>
      </ContentFooter>
    </ContentContainer>
  )
}

function isImage(name = '') {
  return ['.jpg', 'jpeg', '.png', '.bmp', '.gif'].some(ext =>
    name.toLowerCase().endsWith(ext)
  )
}

function isPDF(name = '') {
  return name.toLowerCase().endsWith('.pdf')
}

function getType(name: string): string {
  name = name || ''

  if (isImage(name)) {
    return 'image'
  }
  if (name && isPDF(name)) {
    return 'pdf'
  }
  return ''
}

function openInPopup(name, uri) {
  if (isImage(name)) {
    window.open(
      `${window.origin}${ROUTES.IMAGE}?${querystring.stringify({
        uri: uri
      })}`,
      `newwindow`,
      'fullscreen=yes'
    )
  } else {
    window.open(uri, `_blank`)
  }
}

type ContentViewerProps = {
  attachment: {
    readUri?: string
    writeUri?: string
    name?: string
  } | null
  rotation: number
  ocrData: any
  ocrEnabled: boolean
  handleExtractionValueClicked: (event) => void
  ocrSearchText: string
  currentZoomLevel: number
  attachmentIndex: number
  hasMultipleAttachments?: boolean
  projectLocale: string
}

function ContentViewer({
  attachment = null,
  rotation,
  ocrData,
  ocrEnabled,
  handleExtractionValueClicked,
  ocrSearchText,
  currentZoomLevel,
  attachmentIndex = 1,
  hasMultipleAttachments = false,
  projectLocale
}: ContentViewerProps) {
  const uri = attachment?.readUri
  const name = attachment?.name
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  if (uri == null) {
    return (
      <HalfWidthCenteredDiv>
        <Typography.Title level={3}>
          Image has not been uploaded{' '}
          <span role="img" aria-label="disappointed face">
            😒
          </span>
        </Typography.Title>

        <Typography.Paragraph>
          If you{"'"}re clever you can upload to here:{' '}
        </Typography.Paragraph>

        <Typography.Paragraph>{attachment?.writeUri}</Typography.Paragraph>
      </HalfWidthCenteredDiv>
    )
  }
  if (isImage(name)) {
    return (
      <ImageViewer
        key={name}
        src={uri}
        rotation={rotation}
        ocrData={ocrData}
        ocrEnabled={ocrEnabled}
        handleExtractionValueClicked={handleExtractionValueClicked}
        ocrSearchText={ocrSearchText}
        currentZoomLevel={currentZoomLevel}
        attachmentIndex={attachmentIndex}
        hasMultipleAttachments={hasMultipleAttachments}
        projectLocale={projectLocale}
      />
    )
  }

  if (isPDF(name)) {
    return (
      <PDFViewer
        src={uri}
        rotation={rotation}
        ref={canvasRef}
        ocrData={ocrData}
        ocrEnabled={ocrEnabled}
        handleExtractionValueClicked={handleExtractionValueClicked}
        ocrSearchText={ocrSearchText}
        currentZoomLevel={currentZoomLevel}
        attachmentIndex={attachmentIndex}
        hasMultipleAttachments={hasMultipleAttachments}
        projectLocale={projectLocale}
      />
    )
  }

  return (
    <Button
      target="_blank"
      href={uri}
      icon={<Icons.DownloadOutlined />}
      style={{ alignSelf: 'center' }}
    >
      Download Unsupported File
    </Button>
  )
}

interface ControlsProps {
  resetZoomLevel: () => void
  increaseZoomLevel: () => void
  decreaseZoomLevel: () => void
  rememberFocus: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  returnFocus: () => void
}

const Controls: React.FC<ControlsProps> = ({
  resetZoomLevel,
  increaseZoomLevel,
  decreaseZoomLevel,
  rememberFocus,
  returnFocus
}) => {
  useGlobalShortcut(Matchers.image.zoomReset, () => resetZoomLevel())
  useGlobalShortcut(Matchers.image.zoomOut, () => decreaseZoomLevel())
  useGlobalShortcut(Matchers.image.zoomIn, () => increaseZoomLevel())

  return (
    <ButtonWrapper className="tools">
      <Button
        onClick={() => {
          resetZoomLevel()
          returnFocus()
        }}
        icon={<Icons.FullscreenOutlined />}
        title="Reset zoom level"
        onMouseEnter={rememberFocus}
      />
      <Button
        onClick={() => {
          increaseZoomLevel()
          returnFocus()
        }}
        icon={<Icons.ZoomInOutlined />}
        title="Zoom In"
        onMouseEnter={rememberFocus}
      />
      <Button
        onClick={() => {
          decreaseZoomLevel()
          returnFocus()
        }}
        icon={<Icons.ZoomOutOutlined />}
        title="Zoom out"
        onMouseEnter={rememberFocus}
      />
    </ButtonWrapper>
  )
}

const ButtonWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  top: 1rem;
  left: 0;
  z-index: 100;
`
const HalfWidthCenteredDiv = styled.div`
  align-self: center;
  max-width: 50%;
`
const ErrorDiv = styled.div`
  color: ${colours.RED};
`
